import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormField } from "@/components/ui/form";
import { useAuth } from "@/contexts/AuthContext";
import { Mail, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import AccountTypeSelection from "@/components/auth/AccountTypeSelection";
import AuthFormField from "@/components/auth/AuthFormField";
import ErrorDisplay from "@/components/auth/ErrorDisplay";
import AuthSubmitButton from "@/components/auth/AuthSubmitButton";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  accountType: z.enum(["user", "startup"], {
    required_error: "Please select an account type",
  }),
});

type FormValues = z.infer<typeof formSchema>;

export interface AuthFormProps {
  mode?: "signin" | "signup";
  onSuccess?: (accountType?: string) => void;
}

const AuthForm: React.FC<AuthFormProps> = ({ mode = "signin", onSuccess }) => {
  const { signIn, signUp } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeMode, setActiveMode] = useState<"signin" | "signup">(mode);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      accountType: "user",
    },
  });

  // Watch for account type changes
  const watchedAccountType = form.watch("accountType");
  
  React.useEffect(() => {
    console.log("Account type changed in form:", watchedAccountType);
  }, [watchedAccountType]);

  const handleSubmit = async (values: FormValues) => {
    setLoading(true);
    setError("");
    console.log("Form submitted with values:", values);
    console.log("Account type being processed:", values.accountType);
    
    // Store account type in localStorage BEFORE authentication
    console.log("Storing account type in localStorage:", values.accountType);
    localStorage.setItem('accountType', values.accountType);
    
    try {
      if (activeMode === "signin") {
        await signIn(values.email, values.password);
        console.log("Sign in successful");
        // Call onSuccess with the account type
        if (onSuccess) onSuccess(values.accountType);
      } else {
        await signUp(values.email, values.password);
        console.log("Sign up successful, account type stored:", values.accountType);
        setError("Account created successfully! Please sign in with your new account.");
        setActiveMode("signin");
        // Keep the account type when switching to signin
        form.reset({
          email: values.email,
          password: "",
          accountType: values.accountType,
        });
        return;
      }
    } catch (err: any) {
      console.error("Auth error:", err);
      if (err.message.includes("User already registered") || err.message.includes("already registered")) {
        setError("This email is already registered. Please sign in instead.");
        setActiveMode("signin");
        // Keep the account type when switching modes
        form.setValue("accountType", values.accountType);
      } else if (err.message.includes("Invalid login credentials")) {
        setError("Invalid email or password. Please check your credentials and try again.");
      } else {
        setError(err.message || "Authentication failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full bg-background/90 backdrop-blur-lg border border-border/50 rounded-2xl md:rounded-3xl shadow-2xl p-4 md:p-8 space-y-6 md:space-y-8">
      <Tabs value={activeMode} onValueChange={(v) => setActiveMode(v as "signin" | "signup")} className="w-full">
        <TabsList className="grid w-full grid-cols-2 mb-6 md:mb-8 bg-muted/30 p-1 rounded-lg md:rounded-xl border border-border/30">
          <TabsTrigger 
            value="signin" 
            className="data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:border data-[state=active]:border-border/50 font-semibold text-sm md:text-base py-2 md:py-3 rounded-md md:rounded-lg transition-all duration-300"
          >
            Sign In
          </TabsTrigger>
          <TabsTrigger 
            value="signup" 
            className="data-[state=active]:bg-background data-[state=active]:shadow-lg data-[state=active]:border data-[state=active]:border-border/50 font-semibold text-sm md:text-base py-2 md:py-3 rounded-md md:rounded-lg transition-all duration-300"
          >
            Create Account
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="signin" className="space-y-6 md:space-y-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 md:space-y-8">
              <ErrorDisplay error={error} />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <AuthFormField
                    field={field}
                    label="Email Address"
                    placeholder="your-email@example.com"
                    type="email"
                    autoComplete="email"
                    icon={Mail}
                    accountType={watchedAccountType}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <AuthFormField
                    field={field}
                    label="Password"
                    placeholder="Enter your password"
                    type="password"
                    autoComplete="current-password"
                    icon={Lock}
                    accountType={watchedAccountType}
                  />
                )}
              />

              <AuthSubmitButton loading={loading} mode={activeMode} />
            </form>
          </Form>
        </TabsContent>
        
        <TabsContent value="signup" className="space-y-6 md:space-y-8">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6 md:space-y-8">
              <ErrorDisplay error={error} />

              <FormField
                control={form.control}
                name="accountType"
                render={({ field }) => <AccountTypeSelection field={field} />}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <AuthFormField
                    field={field}
                    label="Email Address"
                    placeholder="your-email@example.com"
                    type="email"
                    autoComplete="email"
                    icon={Mail}
                    accountType={watchedAccountType}
                  />
                )}
              />

              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <AuthFormField
                    field={field}
                    label="Password"
                    placeholder="Password (minimum 6 characters)"
                    type="password"
                    autoComplete="new-password"
                    icon={Lock}
                    accountType={watchedAccountType}
                  />
                )}
              />

              <AuthSubmitButton loading={loading} mode="signup" />
            </form>
          </Form>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AuthForm;
